import { createStore } from "@tanstack/store"
import type { EasyMatchPendingFeedback } from "@/entities/easy-match"
import type { SocialMatchPendingFeedback } from "@/entities/social-match"
import type { Intent } from "@/entities/user"
import { registerSessionCleanup } from "@/shared/lib/auth"
import type { GenderOption } from "./constants"
import { preloadPopupRenderer } from "./renderer-registry"
import type { AgeGateVariant, PopupContentType, PopupStateType } from "./types"

const initialState: PopupStateType = {
	currentPopup: null,
	closing: false,
	popupQueue: [],
}

export const bottomPopupStore = createStore(initialState)

/** Discard callbacks and queued account data without running popup actions. */
function resetPopups(): void {
	bottomPopupStore.setState(() => initialState)
}

/** Показывает попап сразу, если активного нет; иначе ставит в конец очереди. */
function presentPopup(popup: PopupContentType): void {
	preloadPopupRenderer(popup.type)
	bottomPopupStore.setState((state) => {
		if (state.currentPopup) {
			return { ...state, popupQueue: [...state.popupQueue, popup] }
		}
		return { ...state, currentPopup: popup }
	})
}

/** Запускает exit-фазу активного попапа (OverlayHost.open=false); во время закрытия — no-op. */
export function hidePopup(): void {
	bottomPopupStore.setState((state) => {
		if (!state.currentPopup || state.closing) return state
		return { ...state, closing: true }
	})
}

/** Завершает закрытие по концу анимации: снимает попап и показывает следующий из очереди. */
export function finishHidePopup(): void {
	bottomPopupStore.setState((state) => {
		if (!state.closing) return state
		const [nextPopup, ...restQueue] = state.popupQueue
		return {
			currentPopup: nextPopup ?? null,
			closing: false,
			popupQueue: restQueue,
		}
	})
}

/** Показывает попап easy-match. */
export function showEasyMatchPopup(
	onAction: (gender: GenderOption) => void,
): void {
	presentPopup({
		type: "easy-match",
		onAction,
	})
}

function isFeedbackQueued(requestId: string): boolean {
	return isQueued(
		(popup) =>
			(popup.type === "easy-match-feedback" ||
				popup.type === "social-match-feedback") &&
			popup.pending.request_id === requestId,
	)
}

export function showEasyMatchFeedbackPopup(
	pending: EasyMatchPendingFeedback,
	onDone: () => void,
): void {
	if (isFeedbackQueued(pending.request_id)) return
	presentPopup({ type: "easy-match-feedback", pending, onDone })
}

export function showSocialMatchFeedbackPopup(
	pending: SocialMatchPendingFeedback,
	onDone: () => void,
): void {
	if (isFeedbackQueued(pending.request_id)) return
	presentPopup({ type: "social-match-feedback", pending, onDone })
}

export function showCreateDemeanPopup(): void {
	presentPopup({
		type: "create-demean",
	})
}

export function showMatchTypePopup(): void {
	presentPopup({
		type: "match-type",
	})
}

function isQueued(matches: (popup: PopupContentType) => boolean): boolean {
	const { currentPopup, closing, popupQueue } = bottomPopupStore.state
	if (popupQueue.some(matches)) return true
	if (currentPopup === null || closing) return false
	return matches(currentPopup)
}

export function showAgeGatePopup(
	variant: AgeGateVariant,
	intent: Intent | null,
): void {
	if (isQueued((popup) => popup.type === "age-gate")) return
	presentPopup({ type: "age-gate", variant, intent })
}

export function showAgeMismatchPopup(): void {
	if (isQueued((popup) => popup.type === "age-mismatch")) return
	presentPopup({ type: "age-mismatch" })
}

export function showTrustGatePopup(): void {
	if (isQueued((popup) => popup.type === "trust-gate")) return
	presentPopup({ type: "trust-gate" })
}

registerSessionCleanup(resetPopups)
